import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, Check, X, Loader2, ShieldCheck, MapPin, Network, Flag, Eye, Users, HeartHandshake, Target,
  HandCoins, Briefcase, CalendarDays, Coffee, Star, Trophy,
} from "lucide-react";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import api, { formatApiError } from "../lib/api";
import { IMAGES } from "../lib/site";

const DIMENSIONS = [
  { icon: MapPin, title: "Local", text: "Un réseau ancré dans le territoire du Boulou et des Pyrénées-Orientales." },
  { icon: Network, title: "Régional", text: "Des échanges et des relations avec les entreprises et acteurs économiques de la région." },
  { icon: Flag, title: "National", text: "Une ouverture permettant d'accompagner le développement du club et ses projets sportifs au-delà du territoire." },
];

const BENEFICES = [
  { icon: Eye, title: "Visibilité", text: "Associer votre image à un club sportif dynamique et à ses équipes, compétitions et événements." },
  { icon: Users, title: "Réseau", text: "Développer des relations et créer des opportunités avec les autres membres du Peloton." },
  { icon: HeartHandshake, title: "Valeurs", text: "Associer votre image à des valeurs de performance, de solidarité, d'engagement et de dépassement de soi." },
  { icon: MapPin, title: "Territoire", text: "Soutenir un club profondément ancré dans son territoire et impliqué dans la vie locale." },
  { icon: Target, title: "Impact", text: "Contribuer au développement du sport, de la formation, du cyclisme féminin et des projets du club." },
];

const MECENAT_NIVEAUX = [
  { slug: "supporter", name: "Supporter Membre", amount: 120 },
  { slug: "fan", name: "Fan", amount: 360 },
  { slug: "access", name: "Access", amount: 750 },
  { slug: "open", name: "Open", amount: 1500 },
  { slug: "elite", name: "Elite", amount: 5000 },
  { slug: "pro", name: "Pro", amount: 15000 },
];

const RENCONTRES = [
  { icon: Star, label: "Événements partenaires" },
  { icon: Users, label: "Rencontres" },
  { icon: CalendarDays, label: "Invitations" },
  { icon: Coffee, label: "Moments de convivialité" },
  { icon: Trophy, label: "Événements sportifs" },
];

function SectionHead({ num, kicker, title, dark = false }) {
  return (
    <Reveal>
      <div className="flex items-center gap-4 mb-6">
        <span className="font-heading text-sm tracking-[0.3em] text-rouge font-semibold">{num}</span>
        <span className="h-px w-12 bg-rouge" />
        {kicker && (
          <span className={`text-xs font-bold uppercase tracking-[0.25em] ${dark ? "text-white/60" : "text-steel"}`}>{kicker}</span>
        )}
      </div>
      <h2 className={`font-heading uppercase font-semibold tracking-tighter leading-[0.95] text-3xl sm:text-5xl ${dark ? "text-white" : "text-navy"}`}>
        {title}
      </h2>
    </Reveal>
  );
}

const inputCls =
  "w-full border border-navy/20 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-steel focus:outline-none focus:border-rouge transition-colors duration-300";
const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy mb-2";

function eur(n) {
  return `${Number(n).toLocaleString("fr-FR")} €`;
}

function DonModal({ don, onClose }) {
  const [form, setForm] = useState({ entreprise: "", nom: "", prenom: "", email: "", telephone: "" });
  const [paying, setPaying] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pay = async (e) => {
    e.preventDefault();
    setPaying(true);
    try {
      const { data } = await api.post("/peloton/checkout", {
        level: don.slug,
        ...(don.slug === "libre" ? { montant_libre: don.amount } : {}),
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPaying(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-navy/90 backdrop-blur-sm flex items-center justify-center p-5"
      onClick={onClose}
      data-testid="don-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 32 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-7 sm:p-9 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} data-testid="don-close" aria-label="Fermer" className="absolute top-4 right-4 text-steel hover:text-rouge transition-colors">
          <X size={22} />
        </button>
        <p className="font-heading uppercase tracking-wider text-xs text-rouge flex items-center gap-2">
          <ShieldCheck size={14} /> Don sécurisé
        </p>
        <h3 className="mt-2 font-heading uppercase font-semibold tracking-tight text-navy text-2xl sm:text-3xl">
          Mécénat — {don.name}
        </h3>
        <p className="mt-1 font-heading font-semibold text-electrik text-xl">{eur(don.amount)}</p>

        <form onSubmit={pay} className="mt-7 space-y-4" data-testid="don-form">
          <div>
            <label htmlFor="don-entreprise" className={labelCls}>Entreprise</label>
            <input id="don-entreprise" data-testid="don-entreprise" required value={form.entreprise} onChange={set("entreprise")} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="don-nom" className={labelCls}>Nom</label>
              <input id="don-nom" data-testid="don-nom" required value={form.nom} onChange={set("nom")} className={inputCls} />
            </div>
            <div>
              <label htmlFor="don-prenom" className={labelCls}>Prénom</label>
              <input id="don-prenom" data-testid="don-prenom" required value={form.prenom} onChange={set("prenom")} className={inputCls} />
            </div>
          </div>
          <div>
            <label htmlFor="don-email" className={labelCls}>Email</label>
            <input id="don-email" data-testid="don-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} />
          </div>
          <div>
            <label htmlFor="don-tel" className={labelCls}>Téléphone</label>
            <input id="don-tel" data-testid="don-telephone" required type="tel" value={form.telephone} onChange={set("telephone")} className={inputCls} />
          </div>

          <div className="bg-mist border border-navy/10 p-5 text-sm space-y-2" data-testid="don-recap">
            <p className="font-heading uppercase tracking-wider text-xs text-steel">Récapitulatif du don</p>
            <p className="flex justify-between gap-4 text-navy/70">
              <span>Mécénat d'entreprise</span><span className="font-semibold text-navy text-right">{don.name}</span>
            </p>
            <p className="flex justify-between gap-4 text-navy/70">
              <span>Montant du don</span><span className="font-semibold text-navy text-right">{eur(don.amount)}</span>
            </p>
          </div>

          <button
            type="submit"
            disabled={paying}
            data-testid="don-submit"
            className="w-full inline-flex items-center justify-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-6 py-4 hover:bg-navy disabled:opacity-60 transition-colors duration-300"
          >
            {paying && <Loader2 size={16} className="animate-spin" />}
            {paying ? "Redirection vers le paiement…" : `Faire un don de ${eur(don.amount)}`}
          </button>
          <p className="text-[11px] text-steel text-center">
            Paiement sécurisé par Stripe. Aucune donnée bancaire n'est stockée par le club.
          </p>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default function Partenaires() {
  const [partners, setPartners] = useState(null);
  const [don, setDon] = useState(null);
  const [libre, setLibre] = useState("");
  const [form, setForm] = useState({
    entreprise: "", nom: "", fonction: "", telephone: "", email: "",
    montant_envisage: "", projet: "", message: "", rdv: false, consent: false,
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => setPartners([]));
  }, []);

  const setF = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const scrollTo = (id) => setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 60);

  const submitLibre = () => {
    const n = Number(String(libre).replace(",", "."));
    if (!n || n < 10) {
      toast.error("Le montant libre minimum est de 10 €.");
      return;
    }
    setDon({ slug: "libre", name: "Don libre", amount: n });
  };

  const submitPartenariat = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/peloton/partenariat", form);
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEO
        title="Le Peloton des Entreprises"
        description="Mécénat d'entreprise et partenariats commerciaux : soutenez le Club Cycliste Le Boulou et contribuez à ses projets sportifs, à la formation et au cyclisme féminin."
      />
      <PageHeader kicker="Partenaires" title="Le Peloton des Entreprises" image={IMAGES.hero} position="center 60%">
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="peloton-benefices">
          {BENEFICES.map((b, i) => (
            <Reveal key={b.title} delay={0.3 + i * 0.08} className="h-full">
              <div className="group bg-white/5 backdrop-blur border border-white/15 p-6 h-full hover:border-rouge transition-[border-color] duration-300">
                <b.icon size={24} className="text-rouge transition-transform duration-300 group-hover:-translate-y-1" />
                <h3 className="mt-4 font-heading uppercase font-semibold tracking-tight text-white text-lg">{b.title}</h3>
                <p className="mt-2.5 text-xs sm:text-sm text-white/60 leading-relaxed">{b.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </PageHeader>

      <section className="py-16 sm:py-24 border-b border-navy/10" data-testid="peloton-intro">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-heading uppercase font-semibold tracking-tight text-rouge text-2xl sm:text-4xl leading-tight">
              Ensemble, faisons avancer le cyclisme.
            </p>
            <p className="mt-8 text-navy/80 text-base sm:text-lg leading-relaxed max-w-3xl">
              Deux façons de vous engager aux côtés du Club Cycliste Le Boulou : le mécénat, pour soutenir
              simplement le club, ou le partenariat commercial, construit ensemble autour de votre projet.
            </p>
          </Reveal>
          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            <Reveal delay={0.1} className="h-full">
              <div className="h-full bg-white border border-navy/10 p-8 sm:p-10 flex flex-col hover:border-rouge transition-[border-color] duration-300" data-testid="parcours-mecenat">
                <HandCoins size={30} className="text-rouge" />
                <h3 className="mt-5 font-heading uppercase font-semibold tracking-tight text-navy text-2xl sm:text-3xl">
                  Soutenir le club par le mécénat
                </h3>
                <p className="mt-4 text-navy/75 text-sm sm:text-base leading-relaxed flex-1">
                  Vous souhaitez soutenir le développement du Club Cycliste Le Boulou et contribuer à nos projets
                  sportifs, à la formation, au cyclisme féminin et au développement du club ?
                </p>
                <button
                  onClick={() => scrollTo("mecenat")}
                  data-testid="parcours-mecenat-cta"
                  className="group mt-7 inline-flex items-center justify-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-navy transition-colors duration-300"
                >
                  Faire un don
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.18} className="h-full">
              <div className="h-full bg-navy text-white p-8 sm:p-10 flex flex-col" data-testid="parcours-partenariat">
                <Briefcase size={30} className="text-rouge" />
                <h3 className="mt-5 font-heading uppercase font-semibold tracking-tight text-2xl sm:text-3xl">
                  Devenir partenaire du club
                </h3>
                <p className="mt-4 text-white/75 text-sm sm:text-base leading-relaxed flex-1">
                  Vous souhaitez construire un partenariat commercial ou associer votre entreprise à un projet
                  sportif du club ? Parlons-en : chaque partenariat fait l'objet d'un contrat définissant les
                  contreparties (visibilité, maillots, événements…).
                </p>
                <button
                  onClick={() => scrollTo("partenariat")}
                  data-testid="parcours-partenariat-cta"
                  className="group mt-7 inline-flex items-center justify-center gap-3 bg-white text-navy font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-rouge hover:text-white transition-colors duration-300"
                >
                  Parlons de votre projet
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="mecenat" className="py-20 sm:py-28 scroll-mt-24" data-testid="mecenat-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHead num="01" kicker="Mécénat d'entreprise" title={<>Faites un don,<br />soutenez le club.</>} />
          <Reveal delay={0.1}>
            <div className="mt-6 space-y-4 text-navy/80 text-base sm:text-lg leading-relaxed max-w-3xl">
              <p>
                Le mécénat est un don : votre entreprise soutient le club et ses projets sans contrepartie
                commerciale. Le club pourra remercier publiquement ses mécènes, dans le respect de cette
                qualification.
              </p>
              <p>
                Pour une visibilité commerciale (logo, maillots, panneaux, événements…), rendez-vous dans le
                parcours « Devenir partenaire » : un contrat de sponsoring sera alors établi ensemble.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-5 items-stretch" data-testid="mecenat-niveaux">
            {MECENAT_NIVEAUX.map((n, i) => (
              <Reveal key={n.slug} delay={i * 0.05} className="h-full">
                <div className="flex flex-col h-full p-7 bg-white border border-navy/10 hover:border-rouge transition-[border-color,transform] duration-300 hover:-translate-y-1" data-testid={`don-niveau-${n.slug}`}>
                  <span className="font-heading text-[11px] tracking-[0.25em] font-semibold uppercase text-electrik">Don de mécénat</span>
                  <h3 className="mt-3 font-heading uppercase font-semibold tracking-tight text-2xl text-navy leading-tight">{n.name}</h3>
                  <p className="mt-2 font-heading font-semibold tracking-tight text-2xl text-navy">{eur(n.amount)}</p>
                  <button
                    onClick={() => setDon({ slug: n.slug, name: n.name, amount: n.amount })}
                    data-testid={`don-niveau-${n.slug}-cta`}
                    className="mt-6 inline-flex items-center justify-center gap-2 bg-navy text-white font-heading uppercase tracking-wider text-xs px-5 py-3 hover:bg-rouge transition-colors duration-300"
                  >
                    Faire un don de {eur(n.amount)}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.12}>
            <div className="mt-6 bg-mist border border-navy/10 p-7 sm:p-9 flex flex-wrap items-end gap-5" data-testid="don-libre-card">
              <div className="flex-1 min-w-56">
                <p className="font-heading uppercase font-semibold tracking-tight text-navy text-xl">Montant libre</p>
                <p className="mt-1 text-sm text-steel">Choisissez librement le montant de votre don (à partir de 10 €).</p>
                <div className="mt-4 flex items-center gap-3 max-w-xs">
                  <input
                    type="number"
                    min="10"
                    step="1"
                    value={libre}
                    onChange={(e) => setLibre(e.target.value)}
                    placeholder="Montant en €"
                    data-testid="don-libre-input"
                    className={inputCls}
                  />
                  <span className="font-heading font-semibold text-navy">€</span>
                </div>
              </div>
              <button
                onClick={submitLibre}
                data-testid="don-libre-cta"
                className="inline-flex items-center justify-center gap-2 bg-navy text-white font-heading uppercase tracking-wider text-xs px-6 py-3.5 hover:bg-rouge transition-colors duration-300"
              >
                Faire un don libre
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-8 bg-navy text-white p-8 sm:p-12 flex flex-wrap items-center justify-between gap-8" data-testid="projet-important">
              <div className="max-w-2xl">
                <h3 className="font-heading uppercase font-semibold tracking-tight text-2xl sm:text-4xl">
                  Vous avez un projet important ?
                </h3>
                <p className="mt-3 text-white/75 text-sm sm:text-base leading-relaxed">
                  Grands dons, projets spécifiques, partenariats d'envergure : ces opérations ne se règlent pas en
                  ligne. Contactez le club pour étudier votre projet ensemble.
                </p>
              </div>
              <button
                onClick={() => scrollTo("partenariat")}
                data-testid="projet-important-cta"
                className="group shrink-0 inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-9 py-5 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
              >
                Contactez-nous
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-white border-y border-navy/10" data-testid="peloton-communaute">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHead num="02" kicker="Une communauté, pas un simple sponsoring" title={<>Plus qu'un engagement,<br />un réseau.</>} />
          <div className="mt-12 grid sm:grid-cols-3 gap-5">
            {DIMENSIONS.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.08}>
                <div className="group bg-white border border-navy/10 p-8 h-full hover:border-electrik transition-[border-color] duration-300">
                  <d.icon size={26} className="text-electrik transition-transform duration-300 group-hover:-translate-y-1" />
                  <h3 className="mt-5 font-heading uppercase font-semibold tracking-tight text-navy text-2xl">{d.title}</h3>
                  <p className="mt-3 text-sm text-steel leading-relaxed">{d.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12">
            {partners && partners.length > 0 ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="partners-grid">
                {partners.map((p, i) => (
                  <a
                    key={p.id || i}
                    href={p.website || "#"}
                    target={p.website ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="flex items-center justify-center border border-navy/10 bg-white px-8 py-10 hover:border-rouge transition-[border-color] duration-300"
                  >
                    {p.logo ? (
                      <img src={p.logo} alt={`Logo ${p.name}`} className="max-h-16 w-auto object-contain" />
                    ) : (
                      <span className="font-heading uppercase tracking-widest text-navy text-lg">{p.name}</span>
                    )}
                  </a>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="partners-slots">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="flex items-center justify-center border border-dashed border-navy/25 px-8 py-12">
                    <span className="font-heading uppercase tracking-[0.2em] text-steel text-sm">Votre logo ici</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-navy py-20 sm:py-28" data-testid="peloton-evenements">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHead num="03" title="Le Peloton, c'est aussi un réseau" dark />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {RENCONTRES.map((r, i) => (
              <Reveal key={r.label} delay={i * 0.05}>
                <div className="group flex items-center gap-4 border border-white/15 p-6 hover:border-rouge transition-[border-color] duration-300">
                  <r.icon size={22} className="text-rouge shrink-0 transition-transform duration-300 group-hover:-translate-y-1" />
                  <span className="font-heading uppercase tracking-wider text-white text-sm sm:text-base">{r.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="partenariat" className="bg-white border-y border-navy/10 py-20 sm:py-28 scroll-mt-24" data-testid="partenariat-section">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <SectionHead num="04" kicker="Sponsoring & partenariat commercial" title="Parlons de votre projet" />
          <Reveal delay={0.1}>
            <p className="mt-6 text-navy/70 text-base max-w-2xl">
              Ce parcours ne déclenche aucun paiement : après votre demande, le club vous recontacte pour qualifier
              votre projet et établir, le cas échéant, un contrat de partenariat définissant montant, durée et
              contreparties (visibilité, logo, maillots, véhicules, panneaux, site internet, événements,
              communication…).
            </p>
          </Reveal>

          {sent ? (
            <Reveal>
              <div className="mt-10 bg-navy text-white p-8 sm:p-10 flex items-start gap-5" data-testid="partenariat-success">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-rouge">
                  <Check size={22} />
                </span>
                <div>
                  <p className="font-heading uppercase font-semibold tracking-tight text-2xl">Demande envoyée</p>
                  <p className="mt-2 text-white/75">Merci pour votre demande. Le club vous recontactera rapidement pour étudier votre projet.</p>
                </div>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.15}>
              <form onSubmit={submitPartenariat} className="mt-10 space-y-5" data-testid="partenariat-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="pt-entreprise" className={labelCls}>Entreprise</label>
                    <input id="pt-entreprise" data-testid="partenariat-entreprise" required value={form.entreprise} onChange={setF("entreprise")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="pt-nom" className={labelCls}>Nom du contact</label>
                    <input id="pt-nom" data-testid="partenariat-nom" required value={form.nom} onChange={setF("nom")} className={inputCls} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="pt-fonction" className={labelCls}>Fonction</label>
                    <input id="pt-fonction" data-testid="partenariat-fonction" value={form.fonction} onChange={setF("fonction")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="pt-tel" className={labelCls}>Téléphone</label>
                    <input id="pt-tel" data-testid="partenariat-telephone" required type="tel" value={form.telephone} onChange={setF("telephone")} className={inputCls} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="pt-email" className={labelCls}>Email</label>
                    <input id="pt-email" data-testid="partenariat-email" required type="email" value={form.email} onChange={setF("email")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="pt-montant" className={labelCls}>Montant envisagé (facultatif)</label>
                    <input id="pt-montant" data-testid="partenariat-montant" value={form.montant_envisage} onChange={setF("montant_envisage")} placeholder="ex. 5 000 €" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label htmlFor="pt-projet" className={labelCls}>Projet souhaité</label>
                  <input id="pt-projet" data-testid="partenariat-projet" value={form.projet} onChange={setF("projet")} placeholder="ex. visibilité maillots, événement, cyclisme féminin…" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="pt-message" className={labelCls}>Message</label>
                  <textarea id="pt-message" data-testid="partenariat-message" rows={5} value={form.message} onChange={setF("message")} className={inputCls} />
                </div>
                <label className="flex items-start gap-3 text-sm text-navy/75 cursor-pointer" data-testid="partenariat-rdv-label">
                  <input
                    type="checkbox"
                    checked={form.rdv}
                    onChange={setF("rdv")}
                    data-testid="partenariat-rdv"
                    className="mt-1 h-4 w-4 accent-[#D90429]"
                  />
                  Je souhaite un rendez-vous avec le club pour étudier mon projet.
                </label>
                <label className="flex items-start gap-3 text-sm text-navy/75 cursor-pointer" data-testid="partenariat-consent-label">
                  <input
                    type="checkbox"
                    required
                    checked={form.consent}
                    onChange={setF("consent")}
                    data-testid="partenariat-consent"
                    className="mt-1 h-4 w-4 accent-[#D90429]"
                  />
                  J'accepte d'être recontacté par le Club Cycliste Le Boulou concernant ma demande.
                </label>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="partenariat-submit"
                  className="inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-9 py-4 hover:bg-navy disabled:opacity-60 transition-colors duration-300"
                >
                  {sending && <Loader2 size={16} className="animate-spin" />}
                  Envoyer ma demande
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>

      <section className="bg-rouge py-20 sm:py-28" data-testid="peloton-cta">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-heading uppercase font-semibold tracking-tighter leading-[0.95] text-white text-4xl sm:text-6xl">
              Ensemble,<br />faisons avancer le cyclisme.
            </h2>
            <p className="mt-7 text-white/85 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Mécénat ou partenariat commercial : il existe une place pour votre entreprise dans notre communauté.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button
                onClick={() => scrollTo("mecenat")}
                data-testid="cta-don"
                className="group inline-flex items-center gap-3 bg-navy text-white font-heading uppercase tracking-wider text-sm px-9 py-5 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
              >
                Faire un don
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>
              <button
                onClick={() => scrollTo("partenariat")}
                data-testid="cta-projet"
                className="inline-flex items-center gap-3 bg-white text-rouge font-heading uppercase tracking-wider text-sm px-9 py-5 hover:bg-navy hover:text-white transition-[background-color,color] duration-300"
              >
                Parlons de votre projet
              </button>
              <a
                href="mailto:ccleboulou@live.fr"
                data-testid="peloton-nous-contacter"
                className="inline-flex items-center gap-3 border border-white/50 text-white font-heading uppercase tracking-wider text-sm px-9 py-5 hover:bg-white hover:text-rouge hover:border-white transition-[background-color,color,border-color] duration-300"
              >
                Nous contacter
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <AnimatePresence>
        {don && <DonModal don={don} onClose={() => setDon(null)} />}
      </AnimatePresence>
    </>
  );
}
